/* LT-28 — Stale-pricing review modal.
 *
 * Opened from the Master Library banner "Review" button. Lists every LIVE
 * library item currently flagged pricing_stale, showing:
 *   • description + phase tags
 *   • current stored rate
 *   • recalculated rate (server-computed on this GET; no writes)
 *   • absolute + percentage delta (amber if |pct| > 15%)
 *   • cause list (materials / labour rates whose costs changed)
 *
 * Actions:
 *   • Accept selected — writes ledger entries (cascade_accepted), updates
 *     recipe snapshots, recomputes rates, clears stale flags.
 *   • Dismiss selected — clears stale flags only; no ledger writes.
 *
 * All state is local; the parent triggers a reload on close.
 */
import React, { useEffect, useMemo, useState } from "react";
import { X, CheckSquare, Square, AlertTriangle, ChevronRight } from "lucide-react";
import { toast } from "sonner";

import { api } from "../lib/api";
import { gbp, ukDate } from "../lib/format";
import { PrimaryButton, QuietButton } from "./UI";

const cls = (...a) => a.filter(Boolean).join(" ");

// Amber highlight threshold — matches LT-27 variance convention.
const AMBER_THRESHOLD_PCT = 15;

export function StaleReviewModal({ open, onClose, onCommitted }) {
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [rows, setRows] = useState([]);
  const [selected, setSelected] = useState(new Set());

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/library/stale");
      setRows(r.data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load stale items");
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      setSelected(new Set());
      load();
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  const allSelected = rows.length > 0 && selected.size === rows.length;
  const someSelected = selected.size > 0 && selected.size < rows.length;

  const toggle = (id) => {
    setSelected((prev) => {
      const nxt = new Set(prev);
      if (nxt.has(id)) nxt.delete(id); else nxt.add(id);
      return nxt;
    });
  };

  const toggleAll = () => {
    if (allSelected) setSelected(new Set());
    else setSelected(new Set(rows.map((r) => r.id)));
  };

  const selectedIds = useMemo(() => Array.from(selected), [selected]);

  const performAccept = async () => {
    if (selectedIds.length === 0) return;
    setBusy(true);
    try {
      const r = await api.post("/library/stale/accept", { ids: selectedIds });
      const n = r.data?.accepted || selectedIds.length;
      toast.success(`Accepted recalculation on ${n} item${n === 1 ? "" : "s"}`);
      onCommitted?.();
      // Refresh in-modal — remaining stale items stay visible.
      setSelected(new Set());
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Accept failed");
    } finally {
      setBusy(false);
    }
  };

  const performDismiss = async () => {
    if (selectedIds.length === 0) return;
    setBusy(true);
    try {
      const r = await api.post("/library/stale/dismiss", { ids: selectedIds });
      const n = r.data?.dismissed || selectedIds.length;
      toast.success(`Dismissed stale flag on ${n} item${n === 1 ? "" : "s"}`);
      onCommitted?.();
      setSelected(new Set());
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Dismiss failed");
    } finally {
      setBusy(false);
    }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[60] bg-ink/40 flex items-start justify-center pt-10 px-4 pb-6"
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
      data-testid="stale-review-modal-backdrop"
    >
      <div
        className="w-full max-h-[85vh] bg-surface border border-line rounded-lg shadow-card overflow-hidden flex flex-col"
        style={{ maxWidth: 1080 }}
        data-testid="stale-review-modal"
      >
        {/* Header */}
        <div className="px-5 py-3.5 border-b border-line flex items-center justify-between">
          <div>
            <h2 className="font-display text-ink" style={{ fontSize: 15, fontWeight: 600 }}>
              Review stale library rates
            </h2>
            <p className="text-ink-3 mt-0.5" style={{ fontSize: 11.5 }}>
              Underlying material or labour costs have changed. Accept the
              recalculation to update the library, or dismiss to keep the
              current rate.
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-ink-3 hover:text-ink"
            data-testid="stale-review-modal-close"
            aria-label="Close"
          >
            <X size={16} />
          </button>
        </div>

        {/* Table */}
        <div className="flex-1 overflow-auto">
          {loading ? (
            <div className="py-12 text-center text-ink-3" style={{ fontSize: 12 }} data-testid="stale-review-loading">
              Loading…
            </div>
          ) : rows.length === 0 ? (
            <div className="py-12 text-center text-ink-3" data-testid="stale-review-empty">
              <div style={{ fontSize: 13, fontWeight: 500 }} className="text-ink-2">
                Nothing to review
              </div>
              <div style={{ fontSize: 11.5 }} className="mt-1">
                No library items are currently flagged as stale.
              </div>
            </div>
          ) : (
            <table className="w-full text-left">
              <thead className="sticky top-0 bg-surface-2/95 backdrop-blur border-b border-line z-10">
                <tr>
                  <th className="col-head px-3 py-2.5 text-center" style={{ width: 36 }}>
                    <input
                      type="checkbox"
                      checked={allSelected}
                      ref={(el) => { if (el) el.indeterminate = someSelected; }}
                      onChange={toggleAll}
                      className="accent-accent cursor-pointer"
                      data-testid="stale-review-select-all"
                      aria-label="Select all"
                    />
                  </th>
                  <th className="col-head px-4 py-2.5">Description</th>
                  <th className="col-head px-4 py-2.5 text-right" style={{ width: 100 }}>Current</th>
                  <th className="col-head px-4 py-2.5 text-right" style={{ width: 110 }}>Recalculated</th>
                  <th className="col-head px-4 py-2.5 text-right" style={{ width: 100 }}>Δ £</th>
                  <th className="col-head px-4 py-2.5 text-right" style={{ width: 80 }}>Δ %</th>
                  <th className="col-head px-4 py-2.5" style={{ width: 260 }}>Cause</th>
                  <th className="col-head px-4 py-2.5" style={{ width: 110 }}>Flagged</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => {
                  const isSel = selected.has(r.id);
                  const dPctAbs = r.delta_pct == null ? 0 : Math.abs(r.delta_pct);
                  const isAmber = dPctAbs > AMBER_THRESHOLD_PCT;
                  return (
                    <tr
                      key={r.id}
                      data-testid={`stale-row-${r.id}`}
                      className={cls(
                        "border-b border-line-soft hover:bg-surface-2/40 cursor-pointer transition-colors",
                        isSel ? "bg-accent-soft/40" : ""
                      )}
                      onClick={(e) => {
                        if (e.target.closest("input[type=checkbox]")) return;
                        toggle(r.id);
                      }}
                    >
                      <td className="px-3 py-3 text-center">
                        <input
                          type="checkbox"
                          checked={isSel}
                          onChange={() => toggle(r.id)}
                          className="accent-accent cursor-pointer"
                          data-testid={`stale-select-${r.id}`}
                          aria-label={`Select ${r.description}`}
                        />
                      </td>
                      <td className="px-4 py-3">
                        <div className="text-ink" style={{ fontSize: 12.5 }}>{r.description}</div>
                        <div className="text-ink-4 font-mono" style={{ fontSize: 10.5 }}>
                          {r.sku || "—"}{r.unit ? ` · ${r.unit}` : ""}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-right font-mono tnum text-ink-2" style={{ fontSize: 12.5 }}>
                        {gbp(r.current_rate)}
                      </td>
                      <td className="px-4 py-3 text-right font-mono tnum text-ink" style={{ fontSize: 12.5, fontWeight: 500 }}>
                        {gbp(r.recalculated_rate)}
                      </td>
                      <td
                        className={cls(
                          "px-4 py-3 text-right font-mono tnum",
                          isAmber ? "text-amber-700" : (r.delta_abs > 0 ? "text-note-red" : r.delta_abs < 0 ? "text-note-green" : "text-ink-3")
                        )}
                        style={{ fontSize: 12 }}
                      >
                        {r.delta_abs > 0 ? "+" : ""}{gbp(r.delta_abs || 0)}
                      </td>
                      <td
                        className={cls(
                          "px-4 py-3 text-right font-mono tnum",
                          isAmber ? "text-amber-700" : "text-ink-3"
                        )}
                        style={{ fontSize: 12 }}
                        data-testid={`stale-delta-pct-${r.id}`}
                      >
                        <span className="inline-flex items-center gap-1 justify-end">
                          {isAmber && <AlertTriangle size={10} />}
                          {r.delta_pct == null ? "—" : `${r.delta_pct > 0 ? "+" : ""}${r.delta_pct.toFixed(2)}%`}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="text-ink-2" style={{ fontSize: 11.5 }}>
                          {(r.causes || []).map((c) => (
                            <div key={c.entity_id} className="flex items-center gap-1">
                              <ChevronRight size={10} className="text-ink-4" />
                              <span
                                className="rounded font-mono uppercase"
                                style={{
                                  padding: "0 4px",
                                  fontSize: 9,
                                  background: c.entity_type === "material" ? "#F0E7D6" : "#DDE7DE",
                                  color: c.entity_type === "material" ? "#7A5C1A" : "#2F4F3E",
                                }}
                              >
                                {c.entity_type === "material" ? "MAT" : "LAB"}
                              </span>
                              <span className="text-ink" style={{ fontSize: 11.5 }}>{c.entity_name}</span>
                            </div>
                          ))}
                        </div>
                      </td>
                      <td className="px-4 py-3 font-mono tnum text-ink-3" style={{ fontSize: 11.5 }}>
                        {ukDate(r.pricing_stale_since)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        {/* Footer */}
        <div className="px-5 py-3 border-t border-line bg-surface-2/60 flex items-center justify-between">
          <div className="text-ink-3" style={{ fontSize: 11.5 }} data-testid="stale-review-selection-count">
            {selected.size} of {rows.length} selected
          </div>
          <div className="flex items-center gap-2">
            <QuietButton onClick={onClose} data-testid="stale-review-close-btn">
              Close
            </QuietButton>
            <QuietButton
              icon={Square}
              onClick={performDismiss}
              disabled={busy || selected.size === 0}
              data-testid="stale-review-dismiss-btn"
            >
              Dismiss selected
            </QuietButton>
            <PrimaryButton
              icon={CheckSquare}
              onClick={performAccept}
              disabled={busy || selected.size === 0}
              data-testid="stale-review-accept-btn"
            >
              Accept selected
            </PrimaryButton>
          </div>
        </div>
      </div>
    </div>
  );
}
